import {
  getRgObjectsOfType,
  RgDataSetID,
  RG_TYPE,
  type Apartment,
  type Module,
  type Plot,
} from "@project-rouge/rg-core";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { TessellationStageSvg } from "src/screens/tessellation-stage-svg";
import {
  DATASET_DEFAULTS,
  DEFAULT_BRIEF,
  runStages,
  STAGES,
  type BriefInputs,
  type StageSnapshot,
} from "src/screens/tessellation-steps-data";
import {
  DAIWA_TYPOLOGIES,
  getDaiwaTypology,
  type DaiwaTypologyId,
} from "src/screens/daiwa-typologies";

// Admin screen that visualises the tessellation pipeline step-by-step.
// You input a brief (defaults pre-filled), click Start, and the screen shows
// the world after each grouped pipeline stage — including the algorithm used
// and how to read the picture.

const DATASETS: RgDataSetID[] = [RgDataSetID.uk_01, RgDataSetID.uk_02];

export const TessellationStepsScreen = observer(function TessellationStepsScreen() {
  const [brief, setBrief] = useState<BriefInputs>(DEFAULT_BRIEF);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<StageSnapshot[] | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const onStart = () => {
    setRunning(true);
    setError(null);
    setSnapshots(null);
    setStepIndex(0);
    setPlaying(false);
    // Defer so the "running" state can paint before the synchronous compute.
    setTimeout(() => {
      try {
        const result = runStages(brief);
        setSnapshots(result);
      } catch (err) {
        console.error("[tessellation-steps] runStages failed", err);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setRunning(false);
      }
    }, 0);
  };

  useEffect(() => {
    if (!playing || !snapshots) return;
    const t = setTimeout(() => {
      if (stepIndex >= snapshots.length - 1) {
        setPlaying(false);
        return;
      }
      setStepIndex((i) => i + 1);
    }, 1400);
    return () => clearTimeout(t);
  }, [playing, stepIndex, snapshots]);

  const currentSnapshot = snapshots?.[stepIndex] ?? null;

  return (
    <div className="h-screen w-screen flex bg-white text-neutral-8 font-mono text-xs">
      <aside className="w-72 shrink-0 border-r border-neutral-3 p-4 overflow-y-auto">
        <h1 className="text-lg font-bold mb-1">Tessellation steps</h1>
        <p className="text-neutral-6 mb-4">
          Watch the aggregator build a building one pipeline stage at a time.
        </p>

        <h2 className="text-sm font-bold mb-2">Brief</h2>
        <div className="flex flex-col gap-2 mb-4">
          <label className="flex flex-col gap-1">
            <span className="text-neutral-6">Dataset</span>
            <div className="flex gap-1">
              {DATASETS.map((id) => (
                <button
                  key={id}
                  type="button"
                  disabled={running}
                  onClick={() => setBrief(DATASET_DEFAULTS[id])}
                  className={`px-2 py-1 rounded ${
                    id === brief.datasetId
                      ? "bg-neutral-8 text-white"
                      : "bg-neutral-3 text-neutral-7 hover:bg-neutral-4"
                  } disabled:opacity-50`}
                >
                  {id}
                </button>
              ))}
            </div>
            {brief.datasetId === RgDataSetID.uk_02 && (
              <span className="text-neutral-5 text-[10px]">
                uk_02 is incomplete — early stages render; later stages may fail to generate.
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-neutral-6">Footprint</span>
            <div className="flex flex-wrap gap-1">
              <button
                type="button"
                disabled={running}
                onClick={() => setBrief({ ...brief, typology: null })}
                className={`px-2 py-1 rounded ${
                  !brief.typology
                    ? "bg-neutral-8 text-white"
                    : "bg-neutral-3 text-neutral-7 hover:bg-neutral-4"
                } disabled:opacity-50`}
              >
                Box
              </button>
              {DAIWA_TYPOLOGIES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={running}
                  onClick={() =>
                    setBrief({
                      ...brief,
                      typology: t.id,
                      floors: t.defaultFloors,
                    })
                  }
                  className={`px-2 py-1 rounded ${
                    brief.typology === t.id
                      ? "bg-neutral-8 text-white"
                      : "bg-neutral-3 text-neutral-7 hover:bg-neutral-4"
                  } disabled:opacity-50`}
                  title={t.description}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {brief.typology && (
              <span className="text-neutral-5 text-[10px]">
                {getDaiwaTypology(brief.typology).description}
              </span>
            )}
          </label>
          {!brief.typology && (
            <>
              <NumberField
                label="Length (m)"
                value={brief.length}
                onChange={(v) => setBrief({ ...brief, length: v })}
                disabled={running}
              />
              <NumberField
                label="Width (m)"
                value={brief.width}
                onChange={(v) => setBrief({ ...brief, width: v })}
                disabled={running}
              />
            </>
          )}
          <NumberField
            label="Floors"
            value={brief.floors}
            onChange={(v) => setBrief({ ...brief, floors: v })}
            disabled={running}
          />
        </div>

        <button
          type="button"
          onClick={onStart}
          disabled={running}
          className="w-full px-3 py-2 rounded bg-neutral-8 text-white font-bold hover:bg-neutral-7 disabled:bg-neutral-4"
        >
          {running ? "Running…" : snapshots ? "Re-run" : "Start"}
        </button>

        {error && <p className="text-danger-7 mt-3 break-words">{error}</p>}

        <button
          type="button"
          onClick={() => setShowInfo((v) => !v)}
          className="w-full mt-6 px-3 py-1 rounded bg-neutral-3 text-neutral-7 hover:bg-neutral-4"
        >
          {showInfo ? "hide design notes" : "design notes"}
        </button>
        {showInfo && <DesignNotes />}
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden">
        {!snapshots && !running && (
          <div className="flex-1 flex items-center justify-center text-neutral-5 italic">
            Set a brief on the left and hit Start.
          </div>
        )}
        {running && (
          <div className="flex-1 flex items-center justify-center text-neutral-6 italic">
            Running aggregation pipeline (Filter → SetBuildingIndexes)…
          </div>
        )}
        {snapshots && currentSnapshot && (
          <>
            <Stepper
              snapshots={snapshots}
              index={stepIndex}
              onIndex={setStepIndex}
              playing={playing}
              onPlay={setPlaying}
            />
            <div className="flex-1 flex overflow-hidden">
              <div className="flex-1 border-r border-neutral-3 p-4 min-w-0">
                <TessellationStageSvg snapshot={currentSnapshot} />
              </div>
              <StepSidePanel snapshot={currentSnapshot} />
            </div>
          </>
        )}
      </main>
    </div>
  );
});

function NumberField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-neutral-6">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="border border-neutral-3 rounded px-2 py-1 disabled:opacity-50"
      />
    </label>
  );
}

function Stepper({
  snapshots,
  index,
  onIndex,
  playing,
  onPlay,
}: {
  snapshots: StageSnapshot[];
  index: number;
  onIndex: (i: number) => void;
  playing: boolean;
  onPlay: (p: boolean) => void;
}) {
  return (
    <div className="border-b border-neutral-3 p-3 flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => onIndex(Math.max(0, index - 1))}
        disabled={index === 0}
        className="px-2 py-1 rounded bg-neutral-3 hover:bg-neutral-4 disabled:opacity-40"
      >
        ← prev
      </button>
      <button
        type="button"
        onClick={() => onPlay(!playing)}
        className="px-2 py-1 rounded bg-neutral-3 hover:bg-neutral-4"
      >
        {playing ? "pause" : "play"}
      </button>
      <button
        type="button"
        onClick={() => onIndex(Math.min(snapshots.length - 1, index + 1))}
        disabled={index === snapshots.length - 1}
        className="px-2 py-1 rounded bg-neutral-3 hover:bg-neutral-4 disabled:opacity-40"
      >
        next →
      </button>
      <div className="flex gap-1 ml-2 flex-wrap">
        {snapshots.map((s, i) => (
          <button
            key={s.stage.id}
            type="button"
            onClick={() => onIndex(i)}
            className={`px-2 py-1 rounded text-[11px] ${
              i === index
                ? "bg-neutral-8 text-white"
                : "bg-neutral-2 text-neutral-7 hover:bg-neutral-3"
            }`}
          >
            {i + 1}. {s.stage.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepSidePanel({ snapshot }: { snapshot: StageSnapshot }) {
  const counts = useMemo(() => getCounts(snapshot), [snapshot]);
  return (
    <aside className="w-80 shrink-0 p-4 overflow-y-auto">
      <h2 className="text-base font-bold mb-1">{snapshot.stage.label}</h2>
      <p className="text-neutral-6 mb-3">
        <b>Algorithm:</b> {snapshot.stage.algo}
      </p>
      <p className="text-neutral-7 mb-3">{snapshot.stage.description}</p>
      {snapshot.stage.notes && (
        <ul className="list-disc pl-5 space-y-1 text-neutral-6 mb-3">
          {snapshot.stage.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}
      <h3 className="text-sm font-bold mt-4 mb-2 border-t border-neutral-3 pt-3">
        Counts at this step
      </h3>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
        {Object.entries(counts)
          .filter(([, v]) => v > 0)
          .map(([k, v]) => (
            <Row key={k} label={k} value={`${v}`} />
          ))}
      </dl>
      <h3 className="text-sm font-bold mt-4 mb-2 border-t border-neutral-3 pt-3">Legend</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 items-center">
        <Sw color="#f3f3f3" />
        <span>plots</span>
        <Sw color="#cc3333" />
        <span>skeleton threads</span>
        <Sw color="#0066cc" />
        <span>muscles</span>
        <Sw color="#a7dbd8" />
        <span>modules</span>
        <Sw color="#555" />
        <span>cores</span>
        <Sw color="#ffd87d" />
        <span>entrances</span>
        <Sw color="#dddddd" />
        <span>corridors</span>
        <Sw color="#bdbdbd" />
        <span>technical spaces</span>
      </dl>
    </aside>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-neutral-6">{label}</dt>
      <dd className="text-right tabular-nums">{value}</dd>
    </>
  );
}

function Sw({ color }: { color: string }) {
  return (
    <span
      className="inline-block w-3 h-3 border border-neutral-4"
      style={{ background: color }}
    />
  );
}

function getCounts(snapshot: StageSnapshot) {
  const obj = snapshot.obj;
  return {
    plots: getRgObjectsOfType<Plot>(obj, RG_TYPE.Plot).length,
    threads:
      (snapshot.meta.mainThreads?.length ?? 0) +
      (snapshot.meta.mainFrozenThreads?.length ?? 0),
    apartments: getRgObjectsOfType<Apartment>(obj, RG_TYPE.Apartment).length,
    modules: getRgObjectsOfType<Module>(obj, RG_TYPE.Module).length,
    cores: getRgObjectsOfType(obj, RG_TYPE.Core).length,
    corridors: getRgObjectsOfType(obj, RG_TYPE.Corridor).length,
    entrances: getRgObjectsOfType(obj, RG_TYPE.Entrance).length,
    technicalSpaces: getRgObjectsOfType(obj, RG_TYPE.TechnicalSpace).length,
  };
}

function DesignNotes() {
  return (
    <div className="border border-neutral-3 rounded bg-neutral-1 p-3 mt-3 text-neutral-7 space-y-2">
      <p>
        <b>Pipeline-faithful, not micro-faithful.</b> Each step shows the world
        <i> after</i> a real pipe in the aggregator (<code>RunPipeline</code> with{" "}
        <code>to: RgPipes.X</code>). It does not animate the decisions <i>inside</i> a stage (BSP
        splits, the skeleton's 640-option look-ahead, NMCS rollouts) — those would need the
        algorithms themselves instrumented.
      </p>
      <p>
        <b>Incremental forward pass.</b> The pipeline mutates the world in place; we advance{" "}
        <code>from</code> after each stage so heavy stages (especially NMCS) aren't repeated.
      </p>
      <p>
        <b>Custom 2D SVG.</b> The Module Explorer's <code>SvgPlan</code> only draws rectangles,
        so the early stages (plots and the skeleton tailor threads) wouldn't render. This screen
        uses a small custom SVG that draws polygons, threads, muscle lines and rects in one
        coherent coordinate system.
      </p>
      <p>
        <b>Simple-box brief.</b> Length × width × floors via <code>CreateBoxWorld</code> — the same
        proven generation path cost-tracking uses. Unit mix stays at the dataset default.
      </p>
    </div>
  );
}
